import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import api from "@/lib/api";
import {
  LayoutDashboard, CalendarDays, Users, Sparkles, Package,
  CreditCard, BarChart3, UserCog, Settings, UserRound, MessageCircle, Bot, CalendarClock,
} from "lucide-react";
import { LAYOUT } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { hasCustomArea } from "@/lib/customAccess";
import { useOnlineBookings } from "@/lib/onlineBookings";

const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard", path: "/dashboard", icon: LayoutDashboard, comingSoon: false, roles: ["admin", "professional", "reception"] },
  { key: "agenda", label: "Agenda", path: "/agenda", icon: CalendarDays, comingSoon: false, roles: ["admin", "professional", "reception"] },
  { key: "pacientes", label: "Pacientes", path: "/pacientes", icon: Users, comingSoon: false, roles: ["admin", "professional", "reception"] },
  { key: "seguimiento", label: "Seguimiento", path: "/seguimiento", icon: CalendarClock, comingSoon: false, roles: ["admin", "professional", "reception"] },
  { key: "conversaciones", label: "Conversaciones", path: "/conversaciones", icon: MessageCircle, comingSoon: false, roles: ["admin", "reception"] },
  { key: "servicios", label: "Servicios", path: "/servicios", icon: Sparkles, comingSoon: false, roles: ["admin", "professional", "reception"] },
  { key: "inventario", label: "Inventario", path: "/inventario", icon: Package, comingSoon: false, roles: ["admin"], area: "inventory" },
  { key: "pagos", label: "Pagos", path: "/pagos", icon: CreditCard, comingSoon: false, roles: ["admin", "professional"], area: "payments" },
  { key: "reportes", label: "Reportes", path: "/reportes", icon: BarChart3, comingSoon: false, roles: ["admin", "professional"], area: "reports" },
  { key: "equipo", label: "Equipo", path: "/equipo", icon: UserCog, comingSoon: false, roles: ["admin"] },
  { key: "profesionales", label: "Profesionales", path: "/profesionales", icon: UserRound, comingSoon: false, roles: ["admin"] },
  { key: "agente", label: "Agente IA", path: "/agente", icon: Bot, comingSoon: false, roles: ["admin"] },
  { key: "configuracion", label: "Configuración", path: "/configuracion", icon: Settings, comingSoon: false, roles: ["admin"] },
];

function canSee(item, user) {
  if (!user) return true;
  // Fix AW (2026-10-27): rol personalizado = solo las secciones que se le marcaron
  if (item.area && hasCustomArea(user, item.area)) return true;
  return item.roles.includes(user.role);
}

// Fix S (2026-10-01): aviso de chats que esperan respuesta: numero en
// "Conversaciones", en el titulo de la pestaña, sonido y aviso del navegador
// (si se activo) cuando entra un mensaje en un chat que espera al equipo.
// Antes solo se veia mirando Conversaciones. El menu se monta dos veces
// (escritorio y celular): la consulta y el sonido son uno solo, compartido.
const ATTENTION_POLL_MS = 20000;
const TITLE_COUNT_RE = /^\(\d+\) /;
const attention = { count: 0, latest: null, ready: false, timer: null, subscribers: new Set(), audio: null };
const permissionListeners = new Set();

function getAudio() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!attention.audio) attention.audio = new Ctx();
  return attention.audio;
}

// El navegador solo deja sonar despues de un clic o una tecla en la pagina.
function unlockAudio() {
  try {
    const ctx = getAudio();
    if (ctx && ctx.state === "suspended" && ctx.resume) ctx.resume().catch(() => {});
  } catch {
    // sin sonido
  }
}

function playBeep() {
  try {
    const ctx = getAudio();
    if (!ctx) return;
    if (ctx.state === "suspended" && ctx.resume) ctx.resume().catch(() => {});
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
  } catch {
    // sin sonido
  }
}

function notificationsSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

function showNotice(n) {
  if (!notificationsSupported() || window.Notification.permission !== "granted") return;
  try {
    const notice = new window.Notification("Mensaje nuevo de WhatsApp", {
      body: n === 1 ? "Hay 1 chat esperando respuesta" : `Hay ${n} chats esperando respuesta`,
      tag: "strani-conversaciones",
    });
    notice.onclick = () => { window.focus(); notice.close(); };
  } catch {
    // algunos navegadores (Chrome en Android) no permiten avisos desde la pagina
  }
}

async function pollAttention() {
  try {
    const { data } = await api.get("/conversations/attention-count");
    const n = Number(data?.count) || 0;
    const latest = data?.latest_at || null;
    const moved = Boolean(latest && (!attention.latest || latest > attention.latest));
    const isNew = attention.ready && n > 0 && (n > attention.count || moved);
    attention.count = n;
    attention.latest = latest;
    attention.ready = true;
    attention.subscribers.forEach((fn) => fn(n));
    if (isNew) {
      playBeep();
      showNotice(n);
    }
  } catch {
    // silencioso: se reintenta en el proximo ciclo
  }
}

function useAttentionCount(enabled) {
  const [count, setCount] = useState(attention.count);
  useEffect(() => {
    if (!enabled) return undefined;
    attention.subscribers.add(setCount);
    setCount(attention.count);
    if (!attention.timer) {
      document.addEventListener("pointerdown", unlockAudio);
      document.addEventListener("keydown", unlockAudio);
      pollAttention();
      attention.timer = setInterval(pollAttention, ATTENTION_POLL_MS);
    }
    return () => {
      attention.subscribers.delete(setCount);
      if (attention.subscribers.size === 0) {
        clearInterval(attention.timer);
        Object.assign(attention, { timer: null, ready: false, count: 0, latest: null });
        document.removeEventListener("pointerdown", unlockAudio);
        document.removeEventListener("keydown", unlockAudio);
        document.title = document.title.replace(TITLE_COUNT_RE, "");
      }
    };
  }, [enabled]);
  return count;
}

function useNotificationPermission() {
  const [permission, setPermission] = useState(notificationsSupported() ? window.Notification.permission : "unsupported");
  useEffect(() => {
    permissionListeners.add(setPermission);
    return () => { permissionListeners.delete(setPermission); };
  }, []);
  const request = async () => {
    unlockAudio();
    try {
      const result = await window.Notification.requestPermission();
      permissionListeners.forEach((fn) => fn(result));
    } catch {
      // el navegador no lo permite
    }
  };
  return [permission, request];
}

export default function Sidebar({ onNavigate }) {
  const { user } = useAuth();
  const items = NAV_ITEMS.filter((item) => canSee(item, user));
  const watchesChats = Boolean(user && ["admin", "reception"].includes(user.role));
  const waiting = useAttentionCount(watchesChats);
  // Fix AR (2026-10-27): reservas nuevas de la pagina, junto a "Agenda"
  const watchesBookings = Boolean(user && ["admin", "reception", "professional"].includes(user.role));
  const { count: newOnline } = useOnlineBookings(watchesBookings);
  const [notifPermission, enableNotifications] = useNotificationPermission();

  useEffect(() => {
    if (!watchesChats) return;
    const base = document.title.replace(TITLE_COUNT_RE, "");
    document.title = waiting > 0 ? `(${waiting}) ${base}` : base;
  }, [waiting, watchesChats]);

  return (
    <div className="h-full flex flex-col bg-[#F3F0EA]">
      <div className="px-6 py-7">
        <h1 className="font-serif text-2xl tracking-[0.15em] text-brand">STRANI</h1>
        <p className="text-xs text-muted-foreground tracking-wide mt-0.5">Just Because</p>
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {items.map((item) => (
          <div key={item.key}>
          <NavLink
            to={item.path}
            end={Boolean(item.end)}
            onClick={onNavigate}
            data-testid={`${LAYOUT.sidebarNavPrefix}${item.key}`}
            className={({ isActive }) =>
              `flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-sans transition-colors duration-200 ${
                isActive
                  ? "bg-brand-light text-brand font-medium"
                  : "text-muted-foreground hover:bg-white hover:text-foreground"
              }`
            }
          >
            <span className="flex items-center gap-3">
              <item.icon className="w-4 h-4 shrink-0" />
              {item.label}
            </span>
            {item.comingSoon && (
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70 shrink-0">Pronto</span>
            )}
            {item.key === "agenda" && newOnline > 0 && (
              <span className="min-w-5 h-5 px-1.5 rounded-full bg-brand text-white text-[11px] font-semibold flex items-center justify-center shrink-0" data-testid={LAYOUT.agendaOnlineBadge} title="Reservas nuevas desde la página">
                {newOnline > 99 ? "99+" : newOnline}
              </span>
            )}
            {item.key === "conversaciones" && waiting > 0 && (
              <span className="min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold flex items-center justify-center shrink-0" data-testid={LAYOUT.conversationsAttentionBadge} title="Chats esperando respuesta">
                {waiting > 99 ? "99+" : waiting}
              </span>
            )}
          </NavLink>
          </div>
        ))}
      </nav>
      {watchesChats && notifPermission === "default" && (
        <button
          type="button"
          onClick={enableNotifications}
          className="mx-3 mb-2 px-3 py-2 rounded-lg text-xs text-left text-brand hover:bg-white transition-colors"
          data-testid={LAYOUT.enableChatNotifications}
        >
          Activar avisos de mensajes nuevos
        </button>
      )}
      <div className="px-6 py-4 text-xs text-muted-foreground/70 border-t border-border">
        Houston, TX &middot; America/Chicago
      </div>
    </div>
  );
}
